import { BusinessRegistrationService } from '../../../../_services/BusinessRegistration.Service';
import {
  BR_STATUS,
  getItemStatusCode,
  isBrItemExpired,
} from '../../../../constants/brStatus';
import { BR_ADMIN_REJECT_REASON_CODES } from '../utils/brAdmin';
import {
  getProceedButtonState,
  isProceedButtonVisible,
} from '../utils/brAdminProceed';
import { invalidateKybAccessGateCache } from '../../../../constants/businessRegistration';

/**
 * 管理端 BR：审批草稿、确认弹窗、AdminProcess。
 * 依赖页面提供：empCompanyId、loadPending、openAlert（或本 mixin 自带 alert）。
 *
 * 「继续」显示由 getProceedButtonState 单点决定（见 brAdminProceed.js）。
 * 审批中(P)整栏隐藏（见 shouldShowAdminActions）；人工审批中(PA)默认显示 Proceed。
 */
export default {
  data() {
    return {
      drafts: {},
      processingId: null,
      /** 本会话已成功「处理」→ 隐藏，直到用户再改草稿 */
      proceedLockedIds: {},
      /** 本会话用户改过草稿 → 允许再点「处理」 */
      proceedUnlockedIds: {},
      showConfirm: false,
      showAlert: false,
      alertMessage: '',
      pendingAction: null,
    };
  },
  computed: {
    rejectReasonOptions() {
      return BR_ADMIN_REJECT_REASON_CODES.map((r) => ({
        code: r.code,
        label: this.$t(r.key),
      }));
    },
    confirmTitleHtml() {
      const action = this.pendingAction?.action;
      if (action === 'REJECT') {
        return this.$t('lbl_EMPBE_BR_Admin_ConfirmRejectTitle');
      }
      return this.$t('lbl_EMPBE_BR_Admin_ConfirmApproveTitle');
    },
    confirmBodyHtml() {
      const action = this.pendingAction?.action;
      const item = this.pendingAction?.item;
      if (action === 'REJECT') {
        return this.$t(this.resolveRejectConfirmBodyKey(item));
      }
      return this.$t(this.resolveApproveConfirmBodyKey(item));
    },
    confirmOkLabel() {
      const action = this.pendingAction?.action;
      if (action === 'REJECT') {
        return this.$t('lbl_EMPBE_BR_Admin_ConfirmRejectOk');
      }
      return this.$t('lbl_EMPBE_BR_Admin_ConfirmApproveOk');
    },
    confirmOkVariant() {
      const action = this.pendingAction?.action;
      return action === 'REJECT' ? 'reject' : 'approve';
    },
  },
  methods: {
    resolveApproveConfirmBodyKey(item) {
      const code = getItemStatusCode(item);
      if (code === BR_STATUS.REJECTED) {
        return 'lbl_EMPBE_BR_Admin_ConfirmApproveBody_FromRejected';
      }
      if (code === BR_STATUS.PENDING_MANUAL) {
        return 'lbl_EMPBE_BR_Admin_ConfirmApproveBody_FromPendingManual';
      }
      return 'lbl_EMPBE_BR_Admin_ConfirmApproveBody_FromPending';
    },
    resolveRejectConfirmBodyKey(item) {
      const code = getItemStatusCode(item);
      if (code === BR_STATUS.REJECTED) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_UpdateReason';
      }
      if (this.isPendingEffectiveApprovedItem?.(item)) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromNotYetEffective';
      }
      if (this.isInEffectApprovedItem?.(item)) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromApproved';
      }
      if (code === BR_STATUS.PENDING_MANUAL) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromPendingManual';
      }
      return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromPending';
    },
    isDraftBarMode(item) {
      const code = getItemStatusCode(item);
      // 审批中(P) 无操作栏；人工审批中(PA) / 已驳回 才走草稿底栏
      if (code === BR_STATUS.PENDING) {
        return false;
      }
      return item?.listKind === 'pending' || code === BR_STATUS.REJECTED;
    },
    ensureDraft(approvalId) {
      if (!this.drafts[approvalId]) {
        this.$set(this.drafts, approvalId, {
          rejectSelected: false,
          reasons: [],
          otherReasonText: '',
          reasonError: false,
          otherReasonError: false,
        });
      }
      return this.drafts[approvalId];
    },
    clearDraft(approvalId) {
      if (this.drafts[approvalId]) {
        this.$delete(this.drafts, approvalId);
      }
    },
    resetProceedLockState() {
      this.proceedLockedIds = {};
      this.proceedUnlockedIds = {};
    },
    getProceedSessionFlags() {
      return {
        proceedLockedIds: this.proceedLockedIds,
        proceedUnlockedIds: this.proceedUnlockedIds,
      };
    },
    /**
     * @param {object|string|number} itemOrId 优先传列表 item（含 statusCode）；仅传 id 时只能看会话锁
     * @returns {boolean} true = 按钮应隐藏（不可点）
     */
    isProceedLocked(itemOrId) {
      return getProceedButtonState(itemOrId, this.getProceedSessionFlags()) === 'hidden';
    },
    showProceedButton(itemOrId) {
      return isProceedButtonVisible(itemOrId, this.getProceedSessionFlags());
    },
    markProceedLocked(approvalId) {
      if (approvalId == null || approvalId === '') {
        return;
      }
      const key = String(approvalId);
      this.$delete(this.proceedUnlockedIds, key);
      this.$set(this.proceedLockedIds, key, true);
    },
    /** 用户改了草稿：取消隐藏，允许再点「处理」 */
    unlockProceed(approvalId) {
      const key = String(approvalId || '');
      if (!key) {
        return;
      }
      this.$delete(this.proceedLockedIds, key);
      this.$set(this.proceedUnlockedIds, key, true);
    },
    toggleReject(item) {
      const approvalId = item?.approvalId;
      if (!approvalId) return;
      const draft = this.ensureDraft(approvalId);
      const willSelect = !draft.rejectSelected;
      // 已生效直驳：取消勾选驳回 → 清草稿并重新锁「继续」
      if (!willSelect && !this.isDraftBarMode(item)) {
        this.clearDraft(approvalId);
        this.markProceedLocked(approvalId);
        return;
      }
      draft.rejectSelected = willSelect;
      draft.reasonError = false;
      draft.otherReasonError = false;
      this.unlockProceed(approvalId);
      if (willSelect) {
        if (!this.isDraftBarMode(item)) {
          draft.reasons = [];
          draft.otherReasonText = '';
          return;
        }
        const hasDraftReason = (draft.reasons && draft.reasons.length > 0)
          || String(draft.otherReasonText || '').trim();
        if (!hasDraftReason) {
          this.initRejectedDraft?.(item);
        }
      }
    },
    toggleReason(approvalId, code, checked) {
      const draft = this.ensureDraft(approvalId);
      const set = new Set(draft.reasons || []);
      if (checked) set.add(code);
      else set.delete(code);
      draft.reasons = Array.from(set);
      draft.reasonError = false;
      if (code === 'OTHER' && !checked) {
        draft.otherReasonText = '';
        draft.otherReasonError = false;
      }
      this.unlockProceed(approvalId);
    },
    onOtherReasonInput(approvalId, value) {
      const draft = this.ensureDraft(approvalId);
      draft.otherReasonText = value;
      draft.otherReasonError = false;
      this.unlockProceed(approvalId);
    },
    buildReasonTextFromDraft(draft) {
      if (!draft) return '';
      const map = Object.fromEntries(
        this.rejectReasonOptions.map((r) => [r.code, r.label]),
      );
      const otherText = String(draft.otherReasonText || '').trim();
      return (draft.reasons || []).map((code) => {
        // OTHER：只存供应商可见的手输正文，不带管理端选项标签
        if (code === 'OTHER') {
          return otherText;
        }
        return map[code] || code;
      }).filter(Boolean).join('; ');
    },
    validateRejectDraft(draft) {
      if (!(draft.reasons || []).length) {
        draft.reasonError = true;
        draft.otherReasonError = false;
        this.openAlert(this.$t('lbl_EMPBE_BR_Admin_RejectReasonRequired'));
        return false;
      }
      const hasOther = (draft.reasons || []).includes('OTHER');
      const otherText = String(draft.otherReasonText || '').trim();
      if (hasOther && !otherText) {
        draft.otherReasonError = true;
        draft.reasonError = false;
        this.openAlert(this.$t('lbl_EMPBE_BR_Admin_Reject_OtherRequired'));
        return false;
      }
      return true;
    },
    onProceed(item) {
      if (this.isProceedLocked(item)) {
        return;
      }
      const draft = this.ensureDraft(item.approvalId);
      const statusCode = getItemStatusCode(item);
      // 审批中(P)：等阿里云机器审，禁止人工通过/驳回（操作栏亦已隐藏）
      if (statusCode === BR_STATUS.PENDING) {
        return;
      }
      /* 过期单不可「通过」：避免同号作废冲掉有效证；可驳回 */
      if (!draft.rejectSelected && isBrItemExpired(item)) {
        this.openAlert(this.$t('lbl_EMPBE_BR_Admin_CannotApproveExpired'));
        return;
      }
      if (this.isLastInEffectApprovedItem?.(item) && !this.canChangeToReject?.(item)
        && !this.isDraftBarMode(item)) {
        this.onRejectLastBlocked();
        return;
      }
      if (draft.rejectSelected) {
        if (!this.validateRejectDraft(draft)) return;
        this.pendingAction = {
          item,
          action: 'REJECT',
          rejectReason: this.buildReasonTextFromDraft(draft),
        };
        this.showConfirm = true;
        return;
      }
      if (!this.isDraftBarMode(item)) {
        return;
      }
      this.pendingAction = { item, action: 'APPROVE', rejectReason: '' };
      this.showConfirm = true;
    },
    onRejectLastBlocked() {
      this.openAlert(this.$t('lbl_EMPBE_BR_Admin_RejectLastBlocked'));
    },
    openAlert(message) {
      this.alertMessage = message;
      this.showAlert = true;
    },
    closeAlert() {
      this.showAlert = false;
      this.alertMessage = '';
    },
    closeConfirm() {
      this.showConfirm = false;
      this.pendingAction = null;
    },
    async confirmProcess() {
      const action = this.pendingAction;
      if (!action) {
        return;
      }
      this.processingId = action.item.approvalId;
      try {
        const res = await BusinessRegistrationService.AdminProcess({
          empCompanyId: this.empCompanyId,
          approvalId: action.item.approvalId,
          action: action.action,
          rejectReason: action.rejectReason,
        });
        if (!res?.isSuccessful) {
          const errMsg = (Array.isArray(res?.systemErrorMsg) && res.systemErrorMsg[0])
            || res?.msg
            || (typeof res?.data === 'string' ? res.data : '')
            || this.$t('lbl_EMPBE_BR_Admin_ProcessFailed');
          this.$toast?.error?.(errMsg);
          this.openAlert(String(errMsg));
          return;
        }
        invalidateKybAccessGateCache();
        if (action.action === 'APPROVE' && action.item.requestKind === 'NAME_CHANGE') {
          this.cachedCurrentNameEn = action.item.displayNameEn || this.cachedCurrentNameEn;
          this.cachedCurrentNameZh = action.item.displayNameZh || this.cachedCurrentNameZh;
        }
        if (action.action === 'REJECT') {
          const kind = String(action.item.requestKind || '').toUpperCase();
          if (kind === 'NAME_CHANGE' || kind === 'NAME_CHANGE_RESULT') {
            const prevEn = String(
              action.item.previousDisplayNameEn || action.item.PreviousDisplayNameEn || '',
            ).trim();
            const prevZh = String(
              action.item.previousDisplayNameZh || action.item.PreviousDisplayNameZh || '',
            ).trim();
            if (prevEn) this.cachedCurrentNameEn = prevEn;
            if (prevZh) this.cachedCurrentNameZh = prevZh;
          }
        }
        this.closeConfirm();
        this.markProceedLocked(action.item.approvalId);
        this.$toast?.success?.(
          action.action === 'REJECT'
            ? (
              getItemStatusCode(action.item) === BR_STATUS.REJECTED
                ? this.$t('lbl_EMPBE_BR_Admin_UpdateRejectReasonSuccess')
                : this.$t('lbl_EMPBE_BR_Admin_RejectSuccess')
            )
            : this.$t('lbl_EMPBE_BR_Admin_ApproveSuccess'),
        );
        await this.loadPending();
      } catch (e) {
        const errMsg = e?.message || this.$t('lbl_EMPBE_BR_Admin_ProcessFailed');
        this.$toast?.error?.(errMsg);
        this.openAlert(String(errMsg));
      } finally {
        this.processingId = null;
      }
    },
  },
};
